import { apiRequest } from '../lib/apiClient';
/**
 * Auth Context - Provides authentication state throughout the app
 *
 * Features:
 * - Magic link authentication (passwordless)
 * - Anonymous sessions for privacy
 * - Account linking to upgrade anonymous to full account
 * - Role-based access control (RBAC)
 * - Organization and subscription management
 */

import { createContext, useContext, useState, useEffect, ReactNode, useCallback, useMemo } from 'react';
import { authService, AuthUser, AuthStatus } from '../lib/authService';
import { syncService } from '../lib/syncService';
import {
  type UserRole,
  type Permission,
  type RoleAssignment,
  type UserSubscription,
  type Organization,
  type PlanFeatures,
  type UsePermissionsResult,
  createPermissionHooks,
  defaultPermissionHooks,
} from '../lib/rbac';

/**
 * Extended user with role and organization info
 */
export interface ExtendedUser extends AuthUser {
  roles: RoleAssignment[];
  primaryRole: UserRole;
  organizationId: string | null;
  cohortCode: string | null;
}

interface AuthContextValue {
  user: AuthUser | null;
  extendedUser: ExtendedUser | null;
  status: AuthStatus;
  isLoading: boolean;
  isAuthenticated: boolean;
  isAnonymous: boolean;

  // Actions
  sendMagicLink: (email: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  startAnonymousSession: () => void;
  linkToEmail: (email: string) => Promise<{ error: Error | null }>;

  // Sync status
  syncStatus: 'synced' | 'pending' | 'offline' | 'error';

  // RBAC
  roles: RoleAssignment[];
  primaryRole: UserRole;
  permissions: UsePermissionsResult;

  // Organization
  organization: Organization | null;
  subscription: UserSubscription | null;

  // Convenience checks
  canDo: (permission: Permission) => boolean;
  canAccessFeature: (feature: keyof PlanFeatures) => boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [syncStatus, setSyncStatus] = useState<'synced' | 'pending' | 'offline' | 'error'>('offline');

  // RBAC state
  const [roles, setRoles] = useState<RoleAssignment[]>([]);
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [subscription, setSubscription] = useState<UserSubscription | null>(null);

  useEffect(() => {
    let generation = 0;
    const unsubAuth = authService.onAuthChange((newUser, newStatus) => {
      const current = ++generation;
      setRoles([]); setOrganization(null); setSubscription(null);
      setUser(newUser); setStatus(newStatus);
      if (newUser && newStatus === 'authenticated') {
        void apiRequest<{ roles: RoleAssignment[]; organization: Organization | null; subscription: UserSubscription | null }>('/account/bootstrap').then(data => {
          if (current !== generation) return;
          setRoles(data.roles); setOrganization(data.organization); setSubscription(data.subscription);
        }).catch(() => { /* Privileged features stay unavailable if authority cannot be verified. */ });
      }
    });
    const unsubSync = syncService.onStatusChange(setSyncStatus);
    return () => { generation++; unsubAuth(); unsubSync(); };
  }, []);

  const sendMagicLink = useCallback(async (email: string) => {
    return authService.sendMagicLink(email);
  }, []);

  const signOut = useCallback(async () => {
    await authService.signOut();
  }, []);

  const startAnonymousSession = useCallback(() => {
    authService.startAnonymousSession();
  }, []);

  const linkToEmail = useCallback(async (email: string) => {
    return authService.linkToEmail(email);
  }, []);

  // Compute primary role
  const primaryRole: UserRole = useMemo(() => {
    if (roles.length === 0) return 'student';

    // Priority order
    const priority: UserRole[] = ['super_admin', 'org_admin', 'counselor', 'parent', 'student', 'managed_child'];
    for (const role of priority) {
      if (roles.some(r => r.role === role)) {
        return role;
      }
    }
    return 'student';
  }, [roles]);

  // Create extended user object
  const extendedUser: ExtendedUser | null = useMemo(() => {
    if (!user) return null;

    return {
      ...user,
      roles,
      primaryRole,
      organizationId: organization?.id ?? null,
      cohortCode: null, // Would be loaded from profile
    };
  }, [user, roles, primaryRole, organization]);

  // Create permission hooks
  const permissions = useMemo(() => {
    if (!extendedUser) return defaultPermissionHooks;

    return createPermissionHooks({
      id: extendedUser.id,
      roles: extendedUser.roles,
      subscription,
      organizationId: extendedUser.organizationId,
      cohortCode: extendedUser.cohortCode,
    });
  }, [extendedUser, subscription]);

  // Convenience methods
  const canDo = useCallback((permission: Permission): boolean => {
    return permissions.canDo(permission);
  }, [permissions]);

  const canAccessFeature = useCallback((feature: keyof PlanFeatures): boolean => {
    return permissions.canAccessFeature(feature);
  }, [permissions]);

  const value: AuthContextValue = {
    user,
    extendedUser,
    status,
    isLoading: status === 'loading',
    isAuthenticated: status === 'authenticated',
    isAnonymous: status === 'anonymous',
    sendMagicLink,
    signOut,
    startAnonymousSession,
    linkToEmail,
    syncStatus,
    // RBAC
    roles,
    primaryRole,
    permissions,
    // Organization
    organization,
    subscription,
    // Convenience
    canDo,
    canAccessFeature,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

/**
 * Hook for permission checking
 */
export function usePermissions(): UsePermissionsResult {
  const { permissions } = useAuth();
  return permissions;
}

/**
 * Hook to check if user has a specific role
 */
export function useRole(role: UserRole): boolean {
  const { roles } = useAuth();
  return roles.some(r => r.role === role);
}

/**
 * Hook to check if user can perform an action
 */
export function useCanDo(permission: Permission): boolean {
  const { canDo } = useAuth();
  return canDo(permission);
}

/**
 * Hook to check feature access
 */
export function useFeatureAccess(feature: keyof PlanFeatures): boolean {
  const { canAccessFeature } = useAuth();
  return canAccessFeature(feature);
}

/**
 * Hook for organization data
 */
export function useOrganization() {
  const { organization, subscription } = useAuth();
  return { organization, subscription };
}

/**
 * Type guard for privileged users
 */
export function useIsPrivileged(): boolean {
  const { permissions } = useAuth();
  return permissions.isPrivileged;
}

/**
 * Hook for user's primary role
 */
export function usePrimaryRole(): UserRole {
  const { primaryRole } = useAuth();
  return primaryRole;
}

